import { ErrorCode, StandardResolutionReasons } from '@openfeature/core';

class OFREPApiError extends Error {
    constructor(error, response, message, options) {
        super(message, options);
        this.error = error;
        this.response = response;
        Object.setPrototypeOf(this, OFREPApiError.prototype);
        this.name = OFREPApiError.name;
    }
}
class OFREPApiFetchError extends OFREPApiError {
    constructor(error, message, options) {
        super(error, undefined, message, options);
        Object.setPrototypeOf(this, OFREPApiFetchError.prototype);
        this.name = OFREPApiFetchError.name;
    }
}
class OFREPApiUnexpectedResponseError extends OFREPApiError {
    constructor(response, message, options) {
        super(undefined, response, message, options);
        Object.setPrototypeOf(this, OFREPApiUnexpectedResponseError.prototype);
        this.name = OFREPApiUnexpectedResponseError.name;
    }
}
class OFREPApiUnauthorizedError extends OFREPApiError {
    constructor(response, message, options) {
        super(undefined, response, message, options);
        Object.setPrototypeOf(this, OFREPApiUnauthorizedError.prototype);
        this.name = OFREPApiUnauthorizedError.name;
        this.message = message !== null && message !== void 0 ? message : 'OFREP request failed: unauthorized';
    }
}
class OFREPForbiddenError extends OFREPApiError {
    constructor(response, message, options) {
        super(undefined, response, message, options);
        Object.setPrototypeOf(this, OFREPForbiddenError.prototype);
        this.name = OFREPForbiddenError.name;
        this.message = message !== null && message !== void 0 ? message : 'OFREP request failed: forbidden';
    }
}
class OFREPApiTooManyRequestsError extends OFREPApiError {
    constructor(response, message, options) {
        super(undefined, response, message, options);
        this.response = response;
        Object.setPrototypeOf(this, OFREPApiTooManyRequestsError.prototype);
        this.name = OFREPApiTooManyRequestsError.name;
        this.requestTime = new Date();
        this.message =
            (message !== null && message !== void 0 ? message : this.retryAfterDate)
                ? `rate limit exceeded, try again after ${this.retryAfterDate}`
                : `rate limit exceeded, try again later`;
    }
    get retryAfterHeader() {
        return this.response.headers.get('Retry-After');
    }
    get retryAfterSeconds() {
        if (!this.retryAfterHeader) {
            return null;
        }
        const retrySeconds = Number.parseInt(this.retryAfterHeader, 10);
        if (!Number.isFinite(retrySeconds)) {
            return null;
        }
        return retrySeconds;
    }
    get retryAfterDate() {
        if (!this.retryAfterHeader) {
            return null;
        }
        if (this.retryAfterSeconds) {
            const retryAfterSeconds = this.retryAfterSeconds;
            if (!retryAfterSeconds) {
                return null;
            }
            return new Date(this.requestTime.getTime() + this.retryAfterSeconds * 1000);
        }
        const date = new Date(this.retryAfterHeader);
        if (isNaN(date.valueOf())) {
            return null;
        }
        return date;
    }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

function isEvaluationSuccessResponse(response) {
    if (!response || typeof response !== 'object') {
        return false;
    }
    return 'value' in response;
}
function isEvaluationFailureResponse(response) {
    if (!response || typeof response !== 'object') {
        return false;
    }
    return ('key' in response &&
        typeof response.key === 'string' &&
        'errorCode' in response &&
        typeof response.errorCode === 'string');
}

function isBulkEvaluationFailureResponse(response) {
    if (!response || typeof response !== 'object') {
        return false;
    }
    return 'errorCode' in response;
}
function isBulkEvaluationSuccessResponse(response) {
    if (!response || typeof response !== 'object') {
        return false;
    }
    return 'flags' in response;
}

const OFREPEvaluationErrorHttpStatuses = [400, 404, 500];
const OFREPEvaluationSuccessHttpStatuses = [200];

/**
 * Builds headers from static and factory, as well as default content type
 * @param options options
 * @returns built headers
 */
function buildHeaders(options_1) {
    return __awaiter(this, arguments, void 0, function* (options, etag = null) {
        var _a;
        return new Headers([
            ['Content-Type', 'application/json; charset=utf-8'],
            ...((options === null || options === void 0 ? void 0 : options.headers) || []),
            ...((yield ((_a = options === null || options === void 0 ? void 0 : options.headersFactory) === null || _a === void 0 ? void 0 : _a.call(options))) || []),
            ...(etag ? [['If-None-Match', etag]] : []),
        ]);
    });
}

function isomorphicFetch() {
    // We need to do this, as fetch needs the window as scope in the browser: https://fetch.spec.whatwg.org/#concept-request-window
    // Without this any request will fail in the browser https://stackoverflow.com/questions/69876859/why-does-bind-fix-failed-to-execute-fetch-on-window-illegal-invocation-err
    if (globalThis) {
        return globalThis.fetch.bind(globalThis);
    }
    else if (window) {
        return window.fetch.bind(window);
    }
    else if (self) {
        self.fetch.bind(self);
    }
    return fetch;
}
const DEFAULT_TIMEOUT_MS = 10000;
class OFREPApi {
    constructor(baseOptions, fetchImplementation = isomorphicFetch()) {
        this.baseOptions = baseOptions;
        this.fetchImplementation = fetchImplementation;
    }
    isJsonMime(response) {
        const contentTypeHeader = response.headers.get('Content-Type');
        return !!contentTypeHeader && OFREPApi.jsonRegex.test(contentTypeHeader);
    }
    static isOFREFErrorHttpStatus(status) {
        return OFREPEvaluationErrorHttpStatuses.includes(status);
    }
    static isOFREFSuccessHttpStatus(status) {
        return OFREPEvaluationSuccessHttpStatuses.includes(status);
    }
    doFetchRequest(req) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            let response;
            try {
                const timeoutMs = (_a = this.baseOptions.timeoutMs) !== null && _a !== void 0 ? _a : DEFAULT_TIMEOUT_MS;
                const controller = new AbortController();
                // Uses a setTimeout instead of AbortSignal.timeout to support older runtimes.
                setTimeout(() => controller.abort(new DOMException(`This signal is timeout in ${timeoutMs}ms`, 'TimeoutError')), timeoutMs);
                response = yield this.fetchImplementation(req, {
                    signal: controller.signal,
                });
            }
            catch (err) {
                throw new OFREPApiFetchError(err, 'The OFREP request failed.', { cause: err });
            }
            if (response.status === 401) {
                throw new OFREPApiUnauthorizedError(response);
            }
            if (response.status === 403) {
                throw new OFREPForbiddenError(response);
            }
            if (response.status === 429) {
                throw new OFREPApiTooManyRequestsError(response);
            }
            if (response.status === 200 && !this.isJsonMime(response)) {
                throw new OFREPApiUnexpectedResponseError(response, 'OFREP did not respond with expected MIME application/json');
            }
            try {
                return { response, body: yield response.json() };
            }
            catch (_b) {
                return { response };
            }
        });
    }
    postEvaluateFlag(flagKey, evaluationRequest) {
        return __awaiter(this, void 0, void 0, function* () {
            let url = `${this.baseOptions.baseUrl}/ofrep/v1/evaluate/flags/${flagKey}`;
            if (this.baseOptions.query) {
                url = url + `?${this.baseOptions.query.toString()}`;
            }
            const request = new Request(url, {
                headers: yield buildHeaders(this.baseOptions),
                method: 'POST',
                body: JSON.stringify(evaluationRequest !== null && evaluationRequest !== void 0 ? evaluationRequest : {}),
            });
            const { response, body } = yield this.doFetchRequest(request);
            if (response.status === 200 && isEvaluationSuccessResponse(body)) {
                return { httpStatus: response.status, value: body, httpResponse: response };
            }
            else if (OFREPApi.isOFREFErrorHttpStatus(response.status) && isEvaluationFailureResponse(body)) {
                return { httpStatus: response.status, value: body, httpResponse: response };
            }
            throw new OFREPApiUnexpectedResponseError(response, 'The OFREP response does not match the expected format');
        });
    }
    postBulkEvaluateFlags(requestBody_1) {
        return __awaiter(this, arguments, void 0, function* (requestBody, etag = null) {
            let url = `${this.baseOptions.baseUrl}/ofrep/v1/evaluate/flags`;
            if (this.baseOptions.query) {
                url = url + `?${this.baseOptions.query.toString()}`;
            }
            const request = new Request(url, {
                headers: yield buildHeaders(this.baseOptions, etag),
                method: 'POST',
                body: JSON.stringify(requestBody !== null && requestBody !== void 0 ? requestBody : {}),
            });
            const { response, body } = yield this.doFetchRequest(request);
            if (response.status === 200 && isBulkEvaluationSuccessResponse(body)) {
                return { httpStatus: response.status, value: body, httpResponse: response };
            }
            else if (response.status === 304) {
                return { httpStatus: response.status, value: undefined, httpResponse: response };
            }
            else if (OFREPApi.isOFREFErrorHttpStatus(response.status) && isBulkEvaluationFailureResponse(body)) {
                return { httpStatus: response.status, value: body, httpResponse: response };
            }
            throw new OFREPApiUnexpectedResponseError(response, 'The OFREP response does not match the expected format');
        });
    }
}
OFREPApi.jsonRegex = new RegExp(/application\/[^+]*[+]?(json);?.*/, 'i');
function handleEvaluationError(resultOrError, defaultValue, callback) {
    callback === null || callback === void 0 ? void 0 : callback(resultOrError);
    if ('value' in resultOrError) {
        const code = resultOrError.value.errorCode || ErrorCode.GENERAL;
        const message = resultOrError.value.errorCode;
        const metadata = resultOrError.value.metadata;
        const resolution = {
            value: defaultValue,
            reason: StandardResolutionReasons.ERROR,
            flagMetadata: metadata,
            errorCode: code,
            errorMessage: message,
        };
        return resolution;
    }
    else {
        if (resultOrError instanceof Error) {
            throw resultOrError;
        }
        else {
            throw new Error('OFREP flag evaluation error', { cause: resultOrError });
        }
    }
}
function toResolutionDetails(result) {
    return {
        value: result.value,
        variant: result.variant,
        reason: result.reason,
        flagMetadata: result.metadata && toFlagMetadata(result.metadata),
    };
}
function toFlagMetadata(metadata) {
    // OFREP metadata is defined as any object but OF metadata is defined as Record<string, string | number | boolean>
    const originalEntries = Object.entries(metadata);
    const onlyPrimitiveEntries = originalEntries.filter(([, value]) => ['string', 'number', 'boolean'].includes(typeof value));
    return Object.fromEntries(onlyPrimitiveEntries);
}

export { OFREPApi, OFREPApiFetchError, OFREPApiTooManyRequestsError, OFREPApiUnauthorizedError, OFREPApiUnexpectedResponseError, OFREPEvaluationErrorHttpStatuses, OFREPEvaluationSuccessHttpStatuses, OFREPForbiddenError, buildHeaders, handleEvaluationError, isBulkEvaluationFailureResponse, isBulkEvaluationSuccessResponse, isEvaluationFailureResponse, isEvaluationSuccessResponse, toFlagMetadata, toResolutionDetails };
